import { Suspense, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Header from './furniture/Header'
import Breadcrumbs from './furniture/Breadcrumbs'
import Footer from './furniture/Footer'
import Loader from './helpers/Loader'
import Alert from './furniture/Alert'
import Skip from './furniture/Skip'

import Accordion from './sections/Accordion'
import BasicContent from './sections/BasicContent'
import BasicContentWithColumns from './sections/BasicContentWithColumns'
import CalloutCard from './sections/CalloutCard'
import CardListCurated from './sections/CardListCurated'
import CardListDynamic from './sections/CardListDynamic'
import ContentFiftybyFifty from './sections/ContentFiftybyFifty'
import HeroBanner from './sections/HeroBanner'
import InformationBanner from './sections/InformationBanner'
import InformationBox from './sections/InformationBox'
import RatesCalculator from './sections/RatesCalculator'
import RatesCalloutCard from './sections/RatesCalloutCard'
import RatesCardList from './sections/RatesCardList'
import SearchResult from './sections/SearchResult'
import SubPageBanner from './sections/SubPageBanner'
import ThirdParty from './sections/ThirdParty'
import RatesCalloutBanner from './sections/RatesCalloutBanner'
import ProductFiftyFiftyContent from './sections/ProductFiftyFiftyContent'
import TagListing from './sections/TagListing'
import TestimonialCarousel from './sections/TestimonialCarousel'
import StepsCards from './sections/StepsCards'
import ArticleHeader from './sections/ArticleHeader'
import ArticleContainer from './sections/ArticleContainer'
import SectionHeader from './sections/SectionHeader'
import JumpToSection from './sections/JumpToSection'
import EventKeyDetails from './sections/EventKeyDetails'
import WayFinder from './sections/WayFinder'
import {
  getComponentNameAndData,
  getComponentAnchorId,
  shouldUseEventKeySidebarLayout,
  partitionForEventKeySidebar,
  getLockedPreviewBoundaryComp,
} from '../helpers/pageComponentLayout'

// ─────────────────────────────────────────────────────────────────────────────
// Paywall teaser settings
//
// These are the only values you need to touch if you want to show more or less
// content before the sign-in gate appears. No logic changes required.
// ─────────────────────────────────────────────────────────────────────────────

// How many top-level page components to show as a teaser on non-article pages
// (e.g. hero banner + section header + one content block = 3).
// thirdParty is always excluded from this count regardless of what you set here.
const LOCKED_PREVIEW_COUNT = 4

// For article pages, the entire article lives inside a single `articleContainer`
// component. This controls how many of its inner content blocks are shown
// before the fade kicks in. Set to 2 if you want a slightly more generous preview.
const LOCKED_ARTICLE_INNER_COUNT = 1

// These component types inside articleContainer are navigation widgets rather
// than real content — they don't make sense as a teaser so we skip them.
const ARTICLE_UTILITY_TYPES = new Set(['jumpToSection'])

// ─────────────────────────────────────────────────────────────────────────────
// Preview slice builder
// ─────────────────────────────────────────────────────────────────────────────

// Trims the full component list down to just the teaser slice that's safe to
// put in the DOM. Everything past the limit is thrown away here — before React
// renders a single node — so there's nothing to reveal in DevTools. CSS is only
// responsible for the visual fade, not for hiding content.
//
// Article pages need special treatment because `articleContainer` is a wrapper
// that holds an array of child blocks. Counting it as one slot would let the
// entire article slip through, so we also cap the inner list separately.
function buildLockedPreviewList(componentsList) {
  // thirdParty is a script-loader added at runtime — never show it in a preview.
  const filtered = componentsList.filter((c) => c.name !== 'thirdParty')

  const hasArticleContainer = filtered.some((c) => c.name === 'articleContainer')

  if (!hasArticleContainer) {
    // Non-article pages: cap the number of outer components shown.
    return filtered.slice(0, LOCKED_PREVIEW_COUNT)
  }

  // Article pages: all outer components are shown — only the content INSIDE
  // articleContainer is locked. Pass every component through but trim the inner
  // list and mark the container as the clip/banner boundary.
  return filtered.map((comp) => {
    if (comp.name !== 'articleContainer') return comp

    const rawArticleComponents = Array.isArray(comp.data?.articleComponents)
      ? comp.data.articleComponents
      : []

    // Strip navigation widgets and take only the first N real content blocks.
    const previewArticleComponents = rawArticleComponents
      .filter((c) => !ARTICLE_UTILITY_TYPES.has(c.componentName))
      .slice(0, LOCKED_ARTICLE_INNER_COUNT)

    return {
      ...comp,
      _isLockedBoundary: true,
      data: {
        ...comp.data,
        articleComponents: previewArticleComponents,
        // Turn off the in-article sidebar — it needs scroll events and the full
        // navigation item list to work, neither of which exist in a teaser.
        sidebarOnOff: false,
      },
    }
  })
}


// ─────────────────────────────────────────────────────────────────────────────
// LockedPage component
// ─────────────────────────────────────────────────────────────────────────────

// This is the only component that knows anything about locked/paywall content.
// Page.jsx is kept completely clean — when a page is blocked, App.jsx renders
// this instead. If you ever need to change how the paywall looks or behaves,
// this file is the only place you need to look.
function LockedPage(props) {
  const [ComponentsList, setComponentsList] = useState([])

  // Header nav state — same pattern as Page.jsx.
  const [menuActive, setMenuActive] = useState(false)
  const [searchActive, setSearchActive] = useState(false)
  const [ctasActive, setCtasActive] = useState(false)

  const deskLogo = props.nav?.siteLogo ?? ''
  const mobLogo = props.nav?.siteLogoMobile ?? ''

  // Per-page "Hide breadcrumbs" toggle (pageBase.hideBreadcrumbs) — same suppression as Page.jsx.
  const breadcrumbs = props.res?.hideBreadcrumbs ? [] : props.breadcrumbs

  // Maps the string component names that come from the API to their React components.
  // Keep this in sync with the registry in Page.jsx when new components are added.
  const components = {
    accordion: Accordion,
    basicContent: BasicContent,
    basicContentWithColumns: BasicContentWithColumns,
    calloutCard: CalloutCard,
    cardListCurated: CardListCurated,
    cardListDynamic: CardListDynamic,
    contentFiftybyFifty: ContentFiftybyFifty,
    heroBanner: HeroBanner,
    informationBanner: InformationBanner,
    informationBox: InformationBox,
    ratesCalculator: RatesCalculator,
    ratesCalloutCard: RatesCalloutCard,
    ratesCardList: RatesCardList,
    searchResult: SearchResult,
    subPageBanner: SubPageBanner,
    thirdParty: ThirdParty,
    ratesCalloutBanner: RatesCalloutBanner,
    productFiftyFiftyContent: ProductFiftyFiftyContent,
    tagListing: TagListing,
    testimonials: TestimonialCarousel,
    stepsCards: StepsCards,
    articleHeader: ArticleHeader,
    articleContainer: ArticleContainer,
    sectionHeader: SectionHeader,
    jumpToSection: JumpToSection,
    eventKeyDetails: EventKeyDetails,
    wayfinderComponent: WayFinder,
  }

  // Components that legitimately have no data payload and should be silently
  // skipped rather than triggering a "no data" warning in the console.
  const noDataExcludeList = ['thirdParty', 'eventKeyDetails']

  // Normalise the raw API component list into the shape we use internally.
  // We intentionally skip pushing the thirdParty script-loader here — it's
  // excluded from the preview list anyway and has no value in a locked context.
  useEffect(() => {
    if (props.content?.length) {
      setComponentsList(() => {
        const newState = []
        props.content.forEach((comp) => {
          const { typeKey, payload, jumpToId } = getComponentNameAndData(comp)
          if (payload) {
            newState.push({ name: typeKey, data: payload, jumpToId })
            return
          }
          console.warn(`No data sent for component ${typeKey ?? '(unknown)'}`)
        })
        return [...newState]
      })
    }
  }, [props.content])

  // Trim the full list down to only the teaser slice before anything is rendered.
  const previewList = buildLockedPreviewList(ComponentsList)
  const { leading, main, sidebar, hasSidebar } = partitionForEventKeySidebar(previewList)
  const useEventSidebarLayout = shouldUseEventKeySidebarLayout(props.res, hasSidebar, leading.length)
  const previewBoundaryComp = getLockedPreviewBoundaryComp(previewList, useEventSidebarLayout)
  // True when an articleContainer has been tagged as the clip boundary.
  // In that case the gradient/clip is placed on the tagged item, not the last list item.
  const lockedBoundaryExists = previewList.some((c) => c._isLockedBoundary)

  // Renders a single component block. The last item in the preview gets two
  // extra things applied to it:
  //   1. locked-preview-clip  — clips the component at a fixed height so the
  //                             gradient always cuts at a clean visual point
  //   2. The gradient overlay — fades the clipped edge to white before the
  //                             sign-in gate appears
  // Event pages with a sidebar: the RHS event-key-details column also gets
  // locked-preview-clip on its wrapper so it matches the main column cap.
  // Removing locked-preview-clip in DevTools only reveals the rest of that
  // one teaser component — not any locked content.
  const renderBlock = (comp, idx) => {
    const { typeKey, payload, jumpToId } = getComponentNameAndData(comp)
    const TagName = typeKey ? components[typeKey] : undefined
    const rowKey = payload?.key ?? typeKey ?? idx

    if (!TagName) {
      console.warn(`Component type ${typeKey ?? '(unknown)'} was not found. Ignoring...`)
      return <span key={rowKey} />
    }
    if (!Object.keys(payload ?? {}).length && !noDataExcludeList.includes(typeKey)) {
      console.warn(`No data for component type ${typeKey} was found. Ignoring...`)
      return <span key={rowKey} />
    }

    const compData = { ...payload }
    const blockKey = compData.key
    delete compData.key
    if (typeKey === 'articleHeader' || typeKey === 'sectionHeader') {
      compData.breadcrumbs = breadcrumbs
    }
    const fromApi = typeof jumpToId === 'string' && jumpToId.trim().length > 0 ? jumpToId.trim() : null
    const componentAnchorId = fromApi ?? getComponentAnchorId(blockKey, idx)

    const isLastPreview = lockedBoundaryExists
      ? comp._isLockedBoundary === true
      : comp === previewBoundaryComp

    const showBreadcrumbs = comp.name === 'sectionHeader'
    return (
      <div key={rowKey}>
        <div
          id={componentAnchorId}
          className={isLastPreview ? 'scroll-mt-24 locked-preview-clip' : 'scroll-mt-24'}
        >
          <TagName {...compData} idx={idx} />
          {/* Gradient sits inside the clip box as the last child so it always
              paints on top of the component content without a z-index fight.
              from-white/0 avoids the grey midpoint you get with `transparent`. */}
          {/* {isLastPreview && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute bottom-0 left-0 right-0 h-52 bg-gradient-to-b from-white/0 to-white z-10"
            />
          )} */}
        </div>
        {/* Breadcrumbs render outside the clipped wrapper — they're navigation chrome,
            not previewable content, so the teaser clip + fade should not hide them. */}
        {showBreadcrumbs && <Breadcrumbs items={breadcrumbs} noBorder hideBelowLg />}
        {/* Article pages: banner renders inline here, inside <main>, immediately
            after the articleContainer preview — satisfying the a11y landmark
            requirement while keeping it flush with the fade rather than at the
            page bottom. Non-article pages use the banner below the Suspense block. */}
        {isLastPreview && lockedBoundaryExists && (
          <section aria-label="Member access required">{props.lockedBanner}</section>
        )}
      </div>
    )
  }

  return (
    <>
      <Skip />

      {/* Full site header — the visitor can still navigate even on a locked page. */}
      <Header
        deskLogo={deskLogo}
        mobLogo={mobLogo}
        ctas={props.nav?.utilityLinks}
        menuActive={menuActive}
        setMenuActive={setMenuActive}
        searchActive={searchActive}
        setSearchActive={setSearchActive}
        ctasActive={ctasActive}
        setCtasActive={setCtasActive}
        nav={props.nav?.items}
        headerDataSource={props.nav?.headerDataSource}
        res={props.res}
      >
        {/* Site-wide alert (set on the Website node, shown on every page). */}
        {props.res.globalEnablePageAlert &&
          (props.res.globalPageAlertTitle || props.res.globalPageAlertDescription) && (
            <Alert
              title={props.res.globalPageAlertTitle}
              type={props.res.globalPageAlertType}
              description={props.res.globalPageAlertDescription}
            />
          )}
        {/* Page-specific alert (set on the individual page node). */}
        {props.res.enablePageAlert && (props.res.pageAlertTitle || props.res.pageAlertDescription) && (
          <Alert
            title={props.res.pageAlertTitle}
            type={props.res.pageAlertType}
            description={props.res.pageAlertDescription}
          />
        )}
      </Header>

      {/* Teaser content — only the preview slice is ever rendered here. */}
      <main id="main" className="locked-content-main">
        <Suspense key="lockedPageSuspender" fallback={<Loader />}>
          <div data-jump-scan-root>
            {previewList.length > 0 &&
              (() => {
                if (!useEventSidebarLayout) {
                  return previewList.map((comp, idx) => renderBlock(comp, idx))
                }

                return (
                  <>
                    {leading.map(({ comp, idx }) => renderBlock(comp, idx))}
                    <div className="container mx-auto px-5 lg:px-0 lg:pb-12 lg:pt-6 xl:pt-8">
                      <div className="flex flex-col gap-10 lg:grid lg:grid-cols-[minmax(0,1fr)_308px] lg:items-start lg:gap-x-8 xl:gap-x-10">
                        <div className="flex min-w-0 flex-col gap-10">{main.map(({ comp, idx }) => renderBlock(comp, idx))}</div>
                        <div className="event-key-details-sidebar locked-preview-clip flex w-full shrink-0 flex-col gap-6 lg:sticky lg:top-24 lg:w-[308px] lg:self-start xl:top-28">
                          {sidebar.map(({ comp, idx }) => renderBlock(comp, idx))}
                        </div>
                      </div>
                    </div>
                  </>
                )
              })()}
          </div>
        </Suspense>
        {/* Non-article pages: banner renders here, inside <main>, after all
            preview components — satisfying the a11y landmark requirement.
            Article pages render the banner inline in renderBlock instead,
            so it sits flush with the articleContainer preview fade. */}
        {!lockedBoundaryExists && (
          <section data-type="locked-banner" aria-label="Member access required">{props.lockedBanner}</section>
        )}
      </main>

      {/* Footer is always shown — it's not member-only content. */}
      {!props.res.disablePrimaryFooter && (
        <Footer
          {...props.footer}
          deskLogo={deskLogo}
          mobLogo={mobLogo}
          disableSticky={props.res.disableUtilityFooter}
          stickyNavPrimary={props.nav?.primaryLink}
          stickyNavSecondary={props.nav?.mobileStickyNavigationItems}
        />
      )}
    </>
  )
}

LockedPage.propTypes = {
  /** Full page data object from the API (res.result). */
  res: PropTypes.object.isRequired,
  /** Navigation data including logo URLs, menu items, and utility links. */
  nav: PropTypes.object,
  /** Breadcrumb items injected after the hero banner. */
  breadcrumbs: PropTypes.array,
  /** Footer data passed straight through to the Footer component. */
  footer: PropTypes.object,
  /** Raw component list from the API — trimmed to a preview slice before rendering. */
  content: PropTypes.array,
  /** The pre-built LockedContentBanner element, constructed and passed in by App.jsx. */
  lockedBanner: PropTypes.node.isRequired,
}

LockedPage.defaultProps = {
  nav: null,
  breadcrumbs: [],
  footer: null,
  content: [],
}

export default LockedPage
